/**
 * Centralized Error Handling Utility
 * Provides friendly, user-readable error messages
 */

// Common error messages for different scenarios
const ERROR_MESSAGES = {
  // Network/Connection Errors
  NETWORK_ERROR: "We're having trouble connecting to our servers. Please check your internet connection and try again.",
  TIMEOUT_ERROR: "The request is taking longer than expected. Please try again in a moment.",
  SERVER_ERROR: "Something went wrong on our end. We're working to fix it. Please try again in a few minutes.",
  
  // Authentication Errors
  UNAUTHORIZED: "Please log in again to continue. Your session may have expired.",
  FORBIDDEN: "You don't have permission to perform this action. Please contact support if you believe this is an error.",
  INVALID_CREDENTIALS: "The email or password you entered is incorrect. Please try again.",
  
  // Validation Errors
  VALIDATION_ERROR: "Please check the information you entered and try again.",
  REQUIRED_FIELD: "This field is required. Please fill it in to continue.",
  INVALID_FORMAT: "Please enter the information in the correct format.",
  
  // Profile/Onboarding Errors
  PROFILE_SAVE_ERROR: "We couldn't save your profile information. Please try again.",
  PROFILE_LOAD_ERROR: "We couldn't load your profile information. Please refresh the page.",
  ONBOARDING_ERROR: "We're having trouble with the onboarding process. Please try again or contact support.",
  
  // Biometrics/Health Errors
  BIOMETRICS_SAVE_ERROR: "We couldn't save your health information. Please try again.",
  BIOMETRICS_LOAD_ERROR: "We couldn't load your health information. Please refresh the page.",
  
  // Appointment Errors
  APPOINTMENT_BOOKING_ERROR: "We couldn't book your appointment. Please try again or contact our support team.",
  APPOINTMENT_CANCELLATION_ERROR: "We couldn't cancel your appointment. Please try again or contact our support team.",
  APPOINTMENT_RESCHEDULE_ERROR: "We couldn't reschedule your appointment. Please try again or contact our support team.",
  
  // Payment Errors
  PAYMENT_ERROR: "We're having trouble processing your payment. Please check your payment information and try again.",
  SUBSCRIPTION_ERROR: "We're having trouble with your subscription. Please contact our support team.",
  
  // Document/Upload Errors
  UPLOAD_ERROR: "We couldn't upload your file. Please try again or contact support if the problem persists.",
  FILE_TOO_LARGE: "The file is too large. Please choose a smaller file (max 10MB).",
  INVALID_FILE_TYPE: "Please choose a valid file type (PDF, JPG, PNG).",
  
  // Intake Form Errors
  INTAKE_SAVE_ERROR: "We couldn't save your intake form. Please try again.",
  INTAKE_LOAD_ERROR: "We couldn't load your intake form. Please refresh the page.",
  
  // Magic Link/OTP Errors
  OTP_ERROR: "We're having trouble sending the verification code. Please try again.",
  MAGIC_LINK_ERROR: "We're having trouble with the magic link. Please try again or contact support.",
  
  // Default fallback
  DEFAULT_ERROR: "Something unexpected happened. Please try again or contact our support team if the problem continues.",
  CONFLICT_ERROR: "This information already exists. Please check your details and try again."
};

// HTTP status code to friendly message mapping
const STATUS_MESSAGES = {
  400: "The information you provided seems to be incorrect. Please check and try again.",
  401: "Please log in again to continue. Your session may have expired.",
  403: "You don't have permission to perform this action. Please contact support if you believe this is an error.",
  404: "The information you're looking for couldn't be found. Please check the details and try again.",
  408: "The request is taking longer than expected. Please try again in a moment.",
  409: "This information already exists. Please check your details and try again.",
  422: "The information you provided is not in the correct format. Please check and try again.",
  429: "You've made too many requests. Please wait a moment and try again.",
  500: "Something went wrong on our end. We're working to fix it. Please try again in a few minutes.",
  502: "We're experiencing technical difficulties. Please try again in a few minutes.",
  503: "Our service is temporarily unavailable. Please try again in a few minutes.",
  504: "The request is taking longer than expected. Please try again in a moment."
};

/**
 * Extract user-friendly error message from API response
 * @param {Error} error - The error object
 * @param {string} context - Context for the error (e.g., 'profile', 'appointment')
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, context = '') => {
  // If error has a user-friendly message from backend
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // If error has details from backend validation
  if (error.response?.data?.details) {
    const details = error.response.data.details;
    const firstError = Object.values(details)[0];
    if (Array.isArray(firstError)) {
      return firstError[0];
    }
    return firstError;
  }
  
  // If error has error field from backend
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  // Check for specific error codes from backend
  if (error.response?.data?.code) {
    const errorCode = error.response.data.code;
    switch (errorCode) {
      case 'token_not_valid':
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 'validation_error':
        return ERROR_MESSAGES.VALIDATION_ERROR;
      case 'duplicate_entry':
        return ERROR_MESSAGES.CONFLICT_ERROR;
      default:
        break;
    }
  }
  
  // Check HTTP status code
  if (error.response?.status) {
    const statusMessage = STATUS_MESSAGES[error.response.status];
    if (statusMessage) {
      return statusMessage;
    }
  }
  
  // Check for network errors
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }
  
  // Context-specific fallback messages
  if (context) {
    const contextKey = `${context.toUpperCase()}_ERROR`;
    if (ERROR_MESSAGES[contextKey]) {
      return ERROR_MESSAGES[contextKey];
    }
  }
  
  // Default fallback
  return ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Extract field-specific validation errors from API response
 * @param {Error} error - The error object
 * @returns {Object} Field-specific error messages
 */
export const getFieldErrors = (error) => {
  const fieldErrors = {};
  
  if (error.response?.data?.details) {
    const details = error.response.data.details;
    Object.keys(details).forEach(field => {
      const errorMessage = Array.isArray(details[field]) 
        ? details[field][0] 
        : details[field];
      fieldErrors[field] = errorMessage;
    });
  }
  
  return fieldErrors;
};

/**
 * Log error for debugging (only in development)
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 */
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
    if (error.response) {
      console.error('Response:', error.response.data);
      console.error('Status:', error.response.status);
    }
  }
};

/**
 * Handle API error with toast notification
 * @param {Error} error - The error object
 * @param {string} context - Context for the error
 * @param {Function} toast - Toast function from react-toastify
 */
export const handleApiError = (error, context = '', toast) => {
  const message = getErrorMessage(error, context);
  logError(error, context);
  
  if (toast) {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }
  
  return message;
};

/**
 * Handle form validation errors
 * @param {Object} errors - Validation errors object
 * @param {Function} toast - Toast function from react-toastify
 */
export const handleValidationErrors = (errors, toast) => {
  const errorMessages = Object.values(errors);
  if (errorMessages.length > 0) {
    const message = errorMessages[0];
    if (toast) {
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }
};

/**
 * Get success message for different actions
 * @param {string} action - The action performed
 * @returns {string} Success message
 */
export const getSuccessMessage = (action) => {
  const successMessages = {
    profile_saved: "Your profile has been saved successfully!",
    profile_updated: "Your profile has been updated successfully!",
    appointment_booked: "Your appointment has been booked successfully!",
    appointment_cancelled: "Your appointment has been cancelled successfully!",
    appointment_rescheduled: "Your appointment has been rescheduled successfully!",
    intake_saved: "Your intake form has been saved successfully!",
    intake_updated: "Your intake form has been updated successfully!",
    document_uploaded: "Your document has been uploaded successfully!",
    biometrics_saved: "Your health information has been saved successfully!",
    biometrics_updated: "Your health information has been updated successfully!",
    documents_saved: "Your documents have been saved successfully!",
    documents_updated: "Your documents have been updated successfully!",
    password_changed: "Your password has been changed successfully!",
    magic_link_sent: "A magic link has been sent to your phone/email!",
    otp_sent: "A verification code has been sent to your phone/email!",
    login_successful: "Welcome back! You've been logged in successfully.",
    logout_successful: "You've been logged out successfully.",
    registration_successful: "Welcome! Your account has been created successfully.",
    default: "Action completed successfully!"
  };
  
  return successMessages[action] || successMessages.default;
};

export default {
  getErrorMessage,
  getFieldErrors,
  logError,
  handleApiError,
  handleValidationErrors,
  getSuccessMessage
};
